import React from 'react';
import { X, Command } from 'lucide-react';
import { motion } from 'framer-motion';

interface ShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const shortcuts = [
  { category: 'Generale', items: [
    { keys: ['⌘', 'S'], action: 'Salva flow' },
    { keys: ['⌘', 'Z'], action: 'Annulla' },
    { keys: ['⌘', 'Y'], action: 'Ripeti' },
    { keys: ['⌘', 'D'], action: 'Duplica selezione' },
    { keys: ['Delete'], action: 'Elimina selezione' },
  ]},
  { category: 'Navigazione', items: [
    { keys: ['Space'], action: 'Pan (tieni premuto)' },
    { keys: ['⌘', '+'], action: 'Zoom in' },
    { keys: ['⌘', '-'], action: 'Zoom out' },
    { keys: ['⌘', '0'], action: 'Zoom reset' },
    { keys: ['⌘', 'A'], action: 'Seleziona tutto' },
  ]},
  { category: 'Flow', items: [
    { keys: ['⌘', 'E'], action: 'Esporta flow' },
    { keys: ['⌘', 'I'], action: 'Importa flow' },
    { keys: ['⌘', 'L'], action: 'Lista flow' },
    { keys: ['⌘', 'T'], action: 'Test flow' },
    { keys: ['Esc'], action: 'Deseleziona' },
  ]},
];

const ShortcutsModal: React.FC<ShortcutsModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Command className="w-6 h-6 text-whatsapp-green" />
              <h2 className="text-2xl font-bold gradient-text">Scorciatoie da Tastiera</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid gap-8">
            {shortcuts.map((category, idx) => (
              <div key={idx}>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                  {category.category}
                </h3>
                <div className="grid gap-2">
                  {category.items.map((shortcut, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-dark-bg"
                    >
                      <span className="text-gray-700 dark:text-gray-300">
                        {shortcut.action}
                      </span>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, keyIdx) => (
                          <React.Fragment key={keyIdx}>
                            <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow">
                              {key}
                            </kbd>
                            {keyIdx < shortcut.keys.length - 1 && (
                              <span className="text-gray-500 mx-1">+</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            Premi <kbd className="px-2 py-1 mx-1 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded">?</kbd> 
            in qualsiasi momento per vedere questa guida
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ShortcutsModal;
